import React, { useState } from 'react';

interface InjuryTooltipProps {
  metric: string;
  value: number;
  children: React.ReactNode;
}

export const InjuryTooltip: React.FC<InjuryTooltipProps> = ({ metric, value, children }) => {
  const [isVisible, setIsVisible] = useState(false);

  const getTooltipContent = () => {
    switch (metric) {
      case 'powerAsymmetry':
        return {
          title: 'Power Asymmetry',
          explanation: 'Measures imbalance in power production between limbs or trials',
          calculation: 'Calculated from coefficient of variation (CV) of power outputs across trials. CV = (Standard Deviation / Mean) × 100. Values >15% indicate potential imbalances.',
          interpretation: value < 15 ? 'Excellent balance' : value < 30 ? 'Moderate imbalance' : 'Significant asymmetry',
          actionable: value > 30 ? 'Focus on unilateral training and movement screening' : 'Continue current training approach',
          prevention: value > 30 ? 'Single-leg exercises, Bulgarian split squats, unilateral carries. Screen for hip/ankle mobility restrictions.' : 'Maintain bilateral training. Monitor for fatigue-induced asymmetries.',
          riskLevel: value < 15 ? 'Low' : value < 30 ? 'Moderate' : 'High'
        };
      case 'fatigueThreshold':
        return {
          title: 'Fatigue Threshold',
          explanation: 'Power level where significant performance decline occurs',
          calculation: 'Based on power drop-off pattern: 100 - (Power Decline % × 2). Power decline = ((First Trial Power - Last Trial Power) / First Trial Power) × 100.',
          interpretation: value > 75 ? 'High resistance' : value > 50 ? 'Moderate resistance' : 'Low resistance',
          actionable: value < 50 ? 'Reduce training volume and increase recovery' : 'Can handle current or increased loads',
          prevention: value < 50 ? 'Implement periodization: 2-3 days recovery between high-intensity sessions. Focus on sleep (8+ hours), nutrition timing.' : 'Maintain current load. Monitor HRV and subjective wellness scores.',
          riskLevel: value > 75 ? 'Low' : value > 50 ? 'Moderate' : 'High'
        };
      case 'overreachingRisk':
        return {
          title: 'Overreaching Risk',
          explanation: 'Deviation from expected performance patterns indicating accumulated fatigue',
          calculation: 'Compares actual vs expected power: Average of |Actual Power - Expected Power| / Expected Power × 100 × 3. Expected power = Force × Velocity from F-V relationship.',
          interpretation: value < 25 ? 'Well recovered' : value < 50 ? 'Mild overreaching' : 'Significant overreaching',
          actionable: value > 50 ? 'Immediate rest and recovery protocol needed' : 'Monitor recovery markers closely',
          prevention: value > 50 ? 'Deload week: 40-60% normal volume. Active recovery, massage, stress management. Reassess in 7-10 days.' : 'Continue monitoring. Ensure 1-2 complete rest days per week.',
          riskLevel: value < 25 ? 'Low' : value < 50 ? 'Moderate' : 'High'
        };
      case 'techniqueBreakdown':
        return {
          title: 'Technique Breakdown',
          explanation: 'Consistency of movement patterns under increasing load or fatigue',
          calculation: '100 - min(Velocity Consistency, R² × 100). Velocity Consistency = 100 - ((Max Velocity - Min Velocity) / Max Velocity × 100). R² shows linear relationship quality.',
          interpretation: value < 25 ? 'Stable technique' : value < 50 ? 'Some variation' : 'Significant breakdown',
          actionable: value > 50 ? 'Focus on technical skill work and load reduction' : 'Continue current technical training',
          prevention: value > 50 ? 'Reduce loads by 20-30%. Focus on movement quality: video analysis, tempo work, motor control drills.' : 'Maintain technique focus. Include skill work in warm-ups.',
          riskLevel: value < 25 ? 'Low' : value < 50 ? 'Moderate' : 'High'
        };
      case 'loadTolerance':
        return {
          title: 'Load Tolerance',
          explanation: 'Ability to maintain performance under increasing external resistance',
          calculation: 'min(100, (Max Load / 40kg) × 100). Normalized to 40kg as high load reference. Shows relative strength capacity vs body weight.',
          interpretation: value > 75 ? 'High tolerance' : value > 50 ? 'Moderate tolerance' : 'Limited tolerance',
          actionable: value < 50 ? 'Progressive strength development needed' : 'Can progress to heavier loads',
          prevention: value < 50 ? 'Strength phase: 3-5 sets × 3-6 reps at 80-90% 1RM. Focus on posterior chain, core stability.' : 'Maintain strength with 1-2 heavy sessions per week. Progress load gradually (+2.5-5kg).',
          riskLevel: value > 75 ? 'Low' : value > 50 ? 'Moderate' : 'High'
        };
      default:
        return {
          title: 'Injury Prevention Metric',
          explanation: 'Monitors risk factors for potential injury',
          interpretation: 'See detailed analysis',
          actionable: 'Follow specific recommendations',
          riskLevel: 'Variable'
        };
    }
  };

  const content = getTooltipContent();
  const getRiskColor = (level: string) => {
    switch (level) {
      case 'Low': return 'text-green-400';
      case 'Moderate': return 'text-yellow-400';
      case 'High': return 'text-red-400';
      default: return 'text-theme-muted';
    }
  };

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && (
        <div className="absolute z-50 w-96 p-4 bg-theme-surface-2 border border-theme-border rounded-lg shadow-lg -top-2 left-full ml-2">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-theme-text">{content.title}</h4>
            <span className={`text-xs px-2 py-1 rounded bg-opacity-20 ${getRiskColor(content.riskLevel)}`}>
              {content.riskLevel} Risk
            </span>
          </div>
          
          <div className="space-y-3 text-sm">
            <div>
              <span className="font-medium text-theme-text">What it measures:</span>
              <p className="text-theme-muted text-xs mt-1">{content.explanation}</p>
            </div>
            
            <div>
              <span className="font-medium text-theme-text">Current status:</span>
              <p className="text-theme-muted text-xs mt-1">{content.interpretation} ({value.toFixed(1)})</p>
            </div>
            
            <div>
              <span className="font-medium text-theme-text">How it's calculated:</span>
              <p className="text-theme-muted text-xs mt-1">{content.calculation}</p>
            </div>
            
            <div>
              <span className="font-medium text-theme-text">Recommendation:</span>
              <p className="text-theme-muted text-xs mt-1">{content.actionable}</p>
            </div>
            
            <div>
              <span className="font-medium text-theme-text">Prevention strategy:</span>
              <p className="text-theme-muted text-xs mt-1">{content.prevention}</p>
            </div>
          </div>
          
          {/* Arrow pointer */}
          <div className="absolute top-4 -left-2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-theme-surface-2"></div>
        </div>
      )}
    </div>
  );
};